import React, { useState, useEffect, useMemo } from 'react'
import { GOOGLE_FONTS, buildGoogleFontsUrl, GoogleFont, FEATURED_FONTS, FONT_STATS } from '../fonts'
import Header from './Header'

const DEFAULT_SAMPLE_TEXT = 'The quick brown fox jumps over the lazy dog'
const FONTS_PER_PAGE = 100

type SortOption = 'featured' | 'alphabetical' | 'popularity'
type FontCategory = 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace'

const FONT_CATEGORIES: FontCategory[] = ['sans-serif', 'serif', 'display', 'handwriting', 'monospace']

interface ExplorerProps {
  isFontFavorited?: (family: string) => boolean
  isFontSortFavorited?: (family: string) => boolean
  toggleFont?: (family: string) => void
  authenticated?: boolean
}

export default function Explorer({ isFontFavorited, isFontSortFavorited, toggleFont, authenticated }: ExplorerProps = {}) {
  const [sampleText, setSampleText] = useState(DEFAULT_SAMPLE_TEXT)
  const [selectedCategories, setSelectedCategories] = useState<Set<FontCategory>>(new Set(FONT_CATEGORIES))
  const [searchQuery, setSearchQuery] = useState('')
  const [fontSize, setFontSize] = useState(32)
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set())
  const [visibleCount, setVisibleCount] = useState(FONTS_PER_PAGE)
  const [sortBy, setSortBy] = useState<SortOption>('featured')

  const toggleCategory = (cat: FontCategory) => {
    setSelectedCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) {
        next.delete(cat)
      } else {
        next.add(cat)
      }
      return next
    })
  }

  const selectAllCategories = () => {
    setSelectedCategories(new Set(FONT_CATEGORIES))
  }

  const clearAllCategories = () => {
    setSelectedCategories(new Set())
  }

  // Filter and sort fonts based on category, search, and sort option
  const filteredFonts = useMemo(() => {
    let fonts = GOOGLE_FONTS.filter(font => {
      const matchesCategory = selectedCategories.has(font.category as FontCategory)
      const matchesSearch = font.family.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesCategory && matchesSearch
    })

    // Sort based on selected option
    if (sortBy === 'featured' && !searchQuery) {
      // Put featured fonts first, then alphabetical
      const featuredSet = new Set(FEATURED_FONTS)
      fonts = [
        ...fonts.filter(f => featuredSet.has(f.family)),
        ...fonts.filter(f => !featuredSet.has(f.family))
      ]
    } else if (sortBy === 'alphabetical') {
      fonts = [...fonts].sort((a, b) => a.family.localeCompare(b.family))
    }

    // Sort favorites first (uses frozen snapshot so toggling doesn't reshuffle)
    if (isFontSortFavorited) {
      fonts = [...fonts].sort((a, b) => {
        const aFav = isFontSortFavorited(a.family)
        const bFav = isFontSortFavorited(b.family)
        if (aFav && !bFav) return -1
        if (!aFav && bFav) return 1
        return 0
      })
    }

    return fonts
  }, [selectedCategories, searchQuery, sortBy, isFontSortFavorited])

  // Get visible fonts
  const visibleFonts = useMemo(() => {
    return filteredFonts.slice(0, visibleCount)
  }, [filteredFonts, visibleCount])

  // Load fonts when they become visible
  useEffect(() => {
    const fontsToLoad = visibleFonts
      .filter(f => !loadedFonts.has(f.family))
      .map(f => f.family)

    if (fontsToLoad.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(fontsToLoad)
      document.head.appendChild(link)

      setLoadedFonts(prev => {
        const next = new Set(prev)
        fontsToLoad.forEach(f => next.add(f))
        return next
      })
    }
  }, [visibleFonts, loadedFonts])

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(FONTS_PER_PAGE)
  }, [selectedCategories, searchQuery, sortBy])

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + FONTS_PER_PAGE, filteredFonts.length))
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header subtitle={`${filteredFonts.length.toLocaleString()} of ${FONT_STATS.total.toLocaleString()} fonts`} />

      {/* Controls */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Controls Row 1 */}
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search fonts..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-600">Categories</label>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllCategories}
                    className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    All
                  </button>
                  <span className="text-xs text-gray-300">|</span>
                  <button
                    onClick={clearAllCategories}
                    className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {FONT_CATEGORIES.map(cat => (
                  <label
                    key={cat}
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-lg cursor-pointer transition-all
                      ${selectedCategories.has(cat)
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.has(cat)}
                      onChange={() => toggleCategory(cat)}
                      className="sr-only"
                    />
                    <span className="capitalize">{cat}</span>
                    <span className={`${selectedCategories.has(cat) ? 'text-gray-300' : 'text-gray-400'}`}>
                      {FONT_STATS.byCategory[cat]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer"
              >
                <option value="featured">Featured First</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>

            {/* Font Size */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Size: {fontSize}px</label>
              <input
                type="range"
                min="16"
                max="96"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-32 cursor-pointer"
              />
            </div>
          </div>

          {/* Sample Text Input */}
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sample Text</label>
            <input
              type="text"
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              placeholder="Type to preview..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Font Grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid gap-4">
          {visibleFonts.map((font) => (
            <FontCard
              key={font.family}
              font={font}
              sampleText={sampleText || DEFAULT_SAMPLE_TEXT}
              fontSize={fontSize}
              isLoaded={loadedFonts.has(font.family)}
              isFeatured={FEATURED_FONTS.includes(font.family)}
              isFavorited={isFontFavorited?.(font.family)}
              onToggleFavorite={toggleFont ? () => toggleFont(font.family) : undefined}
              authenticated={authenticated}
            />
          ))}
        </div>

        {/* Load More */}
        {visibleCount < filteredFonts.length && (
          <div className="mt-8 text-center">
            <button
              onClick={loadMore}
              className="px-6 py-3 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
            >
              Load More ({(filteredFonts.length - visibleCount).toLocaleString()} remaining)
            </button>
          </div>
        )}

        {filteredFonts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No fonts found matching your criteria.</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-sm text-gray-500 text-center">
            Powered by{' '}
            <a
              href="https://github.com/fontsource/google-font-metadata"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline cursor-pointer"
            >
              google-font-metadata
            </a>
            {' '}· Built by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline cursor-pointer">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

interface FontCardProps {
  font: GoogleFont
  sampleText: string
  fontSize: number
  isLoaded: boolean
  isFeatured: boolean
  isFavorited?: boolean
  onToggleFavorite?: () => void
  authenticated?: boolean
}

function FontCard({ font, sampleText, fontSize, isLoaded, isFeatured, isFavorited, onToggleFavorite, authenticated }: FontCardProps) {
  const [copied, setCopied] = useState(false)

  const copyFontName = async () => {
    await navigator.clipboard.writeText(font.family)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const copyCssImport = async () => {
    const cssImport = `@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(font.family)}:wght@${font.variants.join(';')}&display=swap');`
    await navigator.clipboard.writeText(cssImport)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const handleFavoriteClick = () => {
    if (!authenticated) {
      window.location.href = `/users/sign-in?return_to=${encodeURIComponent(window.location.pathname)}`
      return
    }
    onToggleFavorite?.()
  }

  const categoryColors: Record<string, string> = {
    'sans-serif': 'bg-blue-100 text-blue-700',
    'serif': 'bg-amber-100 text-amber-700',
    'display': 'bg-purple-100 text-purple-700',
    'handwriting': 'bg-pink-100 text-pink-700',
    'monospace': 'bg-green-100 text-green-700',
  }

  return (
    <div className={`bg-white rounded-xl border ${isFeatured ? 'border-gray-300' : 'border-gray-200'} p-5 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-gray-900">{font.family}</h3>
          <span className={`px-2 py-0.5 text-xs rounded-full ${categoryColors[font.category]}`}>
            {font.category}
          </span>
          {isFeatured && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-900 text-white">
              Popular
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onToggleFavorite !== undefined && (
            <button
              onClick={handleFavoriteClick}
              className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer group"
              title={authenticated ? (isFavorited ? 'Remove from favorites' : 'Add to favorites') : 'Sign in to favorite'}
            >
              {isFavorited ? (
                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400 group-hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
              )}
            </button>
          )}
          <button
            onClick={copyCssImport}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="Copy CSS @import"
          >
            {copied ? 'Copied!' : 'Copy CSS'}
          </button>
          <button
            onClick={copyFontName}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
          >
            {copied ? 'Copied!' : 'Copy name'}
          </button>
        </div>
      </div>

      <div
        className="text-gray-800 leading-relaxed transition-opacity duration-300 break-words"
        style={{
          fontFamily: isLoaded ? `'${font.family}', ${font.category === 'monospace' ? 'monospace' : 'sans-serif'}` : 'sans-serif',
          fontSize: `${fontSize}px`,
          opacity: isLoaded ? 1 : 0.5,
        }}
      >
        {sampleText}
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
        <span>Weights: {font.variants.join(', ')}</span>
        {font.subsets.length > 1 && (
          <span>Subsets: {font.subsets.slice(0, 5).join(', ')}{font.subsets.length > 5 ? ` +${font.subsets.length - 5}` : ''}</span>
        )}
      </div>
    </div>
  )
}
